<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'BtnReportInvoiceData',
    inheritAttrs: false,
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'success']);

  const props = defineProps({
    resource: {
      type: Object,
      required: true,
    },
    confirmMessage: {
      type: String,
      required: false,
    },
    size: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const messages = ref({
    bntText: $fn.value.pgettext('BtnReportInvoiceData', 'NAV data submission'),
    btnTooltip: $fn.value.pgettext(
      'BtnReportInvoiceData',
      'NAV data submission'
    ),
    confirmHeader: $fn.value.pgettext(
      'BtnReportInvoiceData',
      'NAV data submission'
    ),
    confirmText: $fn.value.pgettext(
      'BtnReportInvoiceData',
      'Please confirm starting the data submission.'
    ),
    btnOk: $fn.value.pgettext('BtnReportInvoiceData', 'Start data submission'),
    btnCancel: $fn.value.pgettext('BtnReportInvoiceData', 'Cancel'),
  });
  const ux = ref([]);

  const $data = computed(() => ({
    messages: messages.value,
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'messages' }, { name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { messages, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.resource,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('resource');
    }
  );
  watch(
    () => props.confirmMessage,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('confirmMessage');
    }
  );
  watch(
    () => props.size,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('size');
    }
  );

  const act_7315092382398562_emit_owkk9gjg4_getActionArgs = async ($event) => {
    return {
      event: 'success',
    };
  };
  const act_7315092382398562_request_z4lgn6uaj_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'sync_invoice',
      data: { id: props.resource.id, target: 'nav' },
    };
  };
  const act_7315092382398562_request_z4lgn6uaj_getConfirm = async ($event) => {
    return {
      header: messages.value.confirmHeader,
      message: props.confirmMessage || messages.value.confirmText,
      btnOk: messages.value.btnOk,
      btnCancel: messages.value.btnCancel,
      severity: 'warning',
    };
  };

  const act_7315092382398562_emit_owkk9gjg4 = new actionTypes.EmitAction({
    actionArgs: act_7315092382398562_emit_owkk9gjg4_getActionArgs,
  });
  const inst_evh_7315092382398562_success_tac8644hb =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_emit_owkk9gjg4,
      event: 'success',
      displayName: 'emit',
    });
  const act_7315092382398562_request_z4lgn6uaj = new actionTypes.RequestAction({
    actionArgs: act_7315092382398562_request_z4lgn6uaj_getActionArgs,
    confirm: act_7315092382398562_request_z4lgn6uaj_getConfirm,
    displayName: 'submit',
    events: [inst_evh_7315092382398562_success_tac8644hb],
  });
  const inst_evh_7315092382398562_clickPreventStop_5jgbnj3ch =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_request_z4lgn6uaj,
      event: 'click.prevent.stop',
      displayName: 'submit',
    });
  const inst_evh_7315092382398562_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7315092382398562_clickPreventStop_5jgbnj3ch],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_success_tac8644hb,
    inst_evh_7315092382398562_clickPreventStop_5jgbnj3ch,
    submit: inst_evh_7315092382398562_clickPreventStop_5jgbnj3ch,
    inst_evh_7315092382398562_clickPreventStop,
  };

  const evh_7315092382398562_success_tac8644hb = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_success_tac8644hb.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_clickPreventStop_5jgbnj3ch = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7315092382398562_clickPreventStop_5jgbnj3ch.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpButton
    :spinning="actions.submit.isRunning"
    variant="primary"
    :size="size"
    :text="messages.bntText"
    :tooltip="messages.btnTooltip"
    @click.prevent.stop="evh_7315092382398562_clickPreventStop($event, {})"
  >
  </ItpButton>
</template>
